import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { Link } from 'react-router-dom';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';

function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isDriver, isSuperAdmin, isOnTrial, getStormEventsUsed, isTrialExpired, TRIAL_STORM_LIMIT } = useAuth();
  const { getAppName } = useConfig();
  const [stats, setStats] = useState({
    totalRoutes: 0,
    activeRoutes: 0,
    totalCustomers: 0,
    pendingStops: 0,
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(null);
  const [weatherView, setWeatherView] = useState('daily'); // 'daily' or 'hourly'
  const [weatherSettings, setWeatherSettings] = useState(null);
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [weatherLocationName, setWeatherLocationName] = useState(null);

  // Financial summary state
  const [financialSummary, setFinancialSummary] = useState(null);
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(true);
  const [financialSummaryError, setFinancialSummaryError] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState('this_month');

  const loadDashboardData = useCallback(async () => {
    try {
      // Load routes (filtered by product type)
      const routesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes?product=${PRODUCT_TYPE}`);
      const routes = routesResponse.data;

      // Load customers (filtered by product type)
      const customersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers?product=${PRODUCT_TYPE}`);
      const customers = customersResponse.data;

      // Calculate stats
      setStats({
        totalRoutes: routes.length,
        activeRoutes: routes.filter(r => r.status === 'in_progress').length,
        totalCustomers: customers.length,
        pendingStops: routes.reduce((acc, r) => acc + (parseInt(r.stop_count) || 0), 0),
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const { PullToRefreshIndicator } = usePullToRefresh(loadDashboardData);

  // Load weather data using coordinates
  const loadWeather = useCallback(async (lat, lon, locationLabel = null) => {
    try {
      setWeatherLoading(true);
      setWeatherError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/weather?lat=${lat}&lon=${lon}`
      );
      setWeather(response.data);
      // Set location name - use provided label or from API response
      if (locationLabel) {
        setWeatherLocationName(locationLabel);
      } else if (response.data?.current?.location) {
        setWeatherLocationName(response.data.current.location);
      }
    } catch (err) {
      console.error('Failed to load weather:', err);
      setWeatherError('Failed to load weather data');
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  // Load financial summary for admins
  const loadFinancialSummary = useCallback(async (period = financialPeriod) => {
    try {
      setFinancialSummaryLoading(true);
      setFinancialSummaryError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/financial-summary?period=${period}&product=${PRODUCT_TYPE}`);
      setFinancialSummary(response.data);
    } catch (err) {
      console.error('Failed to load financial summary:', err);
      setFinancialSummaryError('Failed to load financial data');
    } finally {
      setFinancialSummaryLoading(false);
    }
  }, [financialPeriod]);

  useEffect(() => {
    loadDashboardData();
    // Only load financial summary for admin users
    if (isAdmin()) {
      loadFinancialSummary();
    }
  }, [loadDashboardData, loadFinancialSummary, isAdmin]);

  // Handle period change for financial summary
  const handleFinancialPeriodChange = (e) => {
    const newPeriod = e.target.value;
    setFinancialPeriod(newPeriod);
    loadFinancialSummary(newPeriod);
  };

  // Load weather settings and then weather data
  useEffect(() => {
    const loadWeatherData = async () => {
      try {
        // First, load weather settings from company
        const settingsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`);
        const settings = {
          weatherUseHome: settingsResponse.data?.weather_use_home || false,
          weatherCity: settingsResponse.data?.weather_city || '',
          weatherState: settingsResponse.data?.weather_state || '',
        };
        setWeatherSettings(settings);

        // If using home location and city/state are set, geocode and load weather
        if (settings.weatherUseHome && settings.weatherCity && settings.weatherState) {
          try {
            const geocodeResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/weather/geocode?city=${encodeURIComponent(settings.weatherCity)}&state=${encodeURIComponent(settings.weatherState)}`
            );
            const locationLabel = `${settings.weatherCity}, ${settings.weatherState}`;
            await loadWeather(geocodeResponse.data.lat, geocodeResponse.data.lon, locationLabel);
            setUsingCurrentLocation(false);
          } catch (err) {
            console.error('Geocode error, falling back to GPS:', err);
            // Fall back to GPS
            loadWeatherFromGPS();
          }
        } else {
          // Use GPS location
          loadWeatherFromGPS();
        }
      } catch (err) {
        console.error('Failed to load weather settings:', err);
        // Fall back to GPS
        loadWeatherFromGPS();
      }
    };

    const loadWeatherFromGPS = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            loadWeather(position.coords.latitude, position.coords.longitude);
            setUsingCurrentLocation(true);
          },
          (error) => {
            console.error('Geolocation error:', error);
            setWeatherError('Enable location to see weather');
            setWeatherLoading(false);
          },
          { timeout: 10000, maximumAge: 300000 } // 5 min cache
        );
      } else {
        setWeatherError('Location not supported');
        setWeatherLoading(false);
      }
    };

    loadWeatherData();
  }, [loadWeather]);

  // Handler for switching to current GPS location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setWeatherLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          loadWeather(position.coords.latitude, position.coords.longitude);
          setUsingCurrentLocation(true);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setWeatherError('Could not get current location');
          setWeatherLoading(false);
        },
        { timeout: 10000 }
      );
    }
  };

  // Handler for switching back to home location
  const handleUseHomeLocation = async () => {
    if (weatherSettings?.weatherCity && weatherSettings?.weatherState) {
      setWeatherLoading(true);
      try {
        const geocodeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/weather/geocode?city=${encodeURIComponent(weatherSettings.weatherCity)}&state=${encodeURIComponent(weatherSettings.weatherState)}`
        );
        const locationLabel = `${weatherSettings.weatherCity}, ${weatherSettings.weatherState}`;
        await loadWeather(geocodeResponse.data.lat, geocodeResponse.data.lon, locationLabel);
        setUsingCurrentLocation(false);
      } catch (err) {
        console.error('Failed to load home location weather:', err);
        setWeatherError('Could not load home location weather');
        setWeatherLoading(false);
      }
    }
  };

  // Get weather icon based on condition code
  // Uses OpenWeatherMap icon codes for compatibility (NWS API maps to these)
  const getWeatherIcon = (iconCode) => {
    // Map icon codes to emoji fallbacks if image fails
    const iconMap = {
      '01d': { url: 'https://openweathermap.org/img/wn/01d@2x.png', emoji: '☀️' },
      '01n': { url: 'https://openweathermap.org/img/wn/01n@2x.png', emoji: '🌙' },
      '02d': { url: 'https://openweathermap.org/img/wn/02d@2x.png', emoji: '⛅' },
      '02n': { url: 'https://openweathermap.org/img/wn/02n@2x.png', emoji: '☁️' },
      '03d': { url: 'https://openweathermap.org/img/wn/03d@2x.png', emoji: '☁️' },
      '03n': { url: 'https://openweathermap.org/img/wn/03n@2x.png', emoji: '☁️' },
      '04d': { url: 'https://openweathermap.org/img/wn/04d@2x.png', emoji: '☁️' },
      '04n': { url: 'https://openweathermap.org/img/wn/04n@2x.png', emoji: '☁️' },
      '09d': { url: 'https://openweathermap.org/img/wn/09d@2x.png', emoji: '🌧️' },
      '09n': { url: 'https://openweathermap.org/img/wn/09n@2x.png', emoji: '🌧️' },
      '10d': { url: 'https://openweathermap.org/img/wn/10d@2x.png', emoji: '🌦️' },
      '10n': { url: 'https://openweathermap.org/img/wn/10n@2x.png', emoji: '🌧️' },
      '11d': { url: 'https://openweathermap.org/img/wn/11d@2x.png', emoji: '⛈️' },
      '11n': { url: 'https://openweathermap.org/img/wn/11n@2x.png', emoji: '⛈️' },
      '13d': { url: 'https://openweathermap.org/img/wn/13d@2x.png', emoji: '❄️' },
      '13n': { url: 'https://openweathermap.org/img/wn/13n@2x.png', emoji: '❄️' },
      '50d': { url: 'https://openweathermap.org/img/wn/50d@2x.png', emoji: '🌫️' },
      '50n': { url: 'https://openweathermap.org/img/wn/50n@2x.png', emoji: '🌫️' }
    };
    const icon = iconMap[iconCode] || iconMap['03d'];
    return icon.url;
  };

  // Format date for forecast
  const formatForecastDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Format time for hourly forecast (handles ISO format from NWS)
  const formatHourlyTime = (timeStr) => {
    const date = new Date(timeStr);
    const now = new Date();
    const hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;

    // Show "Now" for current hour
    if (date.getHours() === now.getHours() && date.getDate() === now.getDate()) {
      return 'Now';
    }
    return `${hour12}${ampm}`;
  };

  // Group hourly forecast by day
  const groupHourlyByDay = (hourlyData) => {
    if (!hourlyData || hourlyData.length === 0) return [];

    const groups = [];
    let currentDay = null;
    let currentGroup = null;

    for (const hour of hourlyData) {
      const date = new Date(hour.time);
      const dayKey = date.toDateString();

      if (dayKey !== currentDay) {
        if (currentGroup) groups.push(currentGroup);
        currentDay = dayKey;
        currentGroup = {
          label: formatForecastDate(hour.time),
          hours: []
        };
      }
      currentGroup.hours.push(hour);
    }
    if (currentGroup) groups.push(currentGroup);

    return groups;
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo">{getAppName()}</span>
          <nav className="header-nav desktop-nav">
            <span className="nav-link active">Dashboard</span>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            {!isAdmin() && <button onClick={() => navigate('/my-routes')} className="nav-link">My History</button>}
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <span className="mobile-nav-link active">Dashboard</span>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            {!isAdmin() && <button onClick={() => { navigate('/my-routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">My History</button>}
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <style>{`
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .help-link {
          width: 28px !important;
          height: 28px !important;
          padding: 0 !important;
          border-radius: 50% !important;
          font-weight: bold !important;
          font-size: 1rem !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: rgba(255,255,255,0.15) !important;
        }
        .help-link:hover {
          background: rgba(255,255,255,0.25) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }

        /* Weather Widget */
        .weather-widget {
          background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
          border-radius: var(--radius-lg);
          padding: 20px;
          margin-top: 20px;
          color: white;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }
        .weather-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255,255,255,0.2);
        }
        .weather-header-location {
          font-weight: 600;
          font-size: 1rem;
        }
        .weather-location-toggle {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .weather-location-toggle:hover {
          background: rgba(255,255,255,0.3);
        }
        .weather-loading, .weather-error {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 20px;
          color: rgba(255,255,255,0.9);
        }
        .weather-error-icon {
          font-size: 1.5rem;
        }
        .weather-current {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .weather-icon-large {
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }
        .weather-temp-main {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 120px;
        }
        .weather-temp {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1;
        }
        .weather-condition {
          font-size: 1rem;
          text-transform: capitalize;
          opacity: 0.9;
          margin-top: 4px;
        }
        .weather-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 0.875rem;
          opacity: 0.9;
        }
        .weather-location {
          font-weight: 600;
          font-size: 1rem;
        }
        .weather-forecast {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid rgba(255,255,255,0.2);
          gap: 8px;
          overflow-x: auto;
        }
        .forecast-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 60px;
          flex: 1;
        }
        .forecast-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          opacity: 0.8;
        }
        .forecast-icon {
          width: 40px;
          height: 40px;
        }
        .forecast-temp {
          font-size: 1rem;
          font-weight: 600;
        }
        .forecast-snow {
          font-size: 0.7rem;
          background: rgba(255,255,255,0.2);
          padding: 2px 6px;
          border-radius: 8px;
          white-space: nowrap;
        }
        .weather-snow-current {
          font-size: 0.85rem;
          background: rgba(255,255,255,0.2);
          padding: 4px 10px;
          border-radius: 12px;
          margin-top: 6px;
        }
        .weather-toggle {
          display: flex;
          justify-content: center;
          gap: 4px;
          margin-top: 16px;
          background: rgba(0,0,0,0.15);
          padding: 4px;
          border-radius: 8px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        .weather-toggle-btn {
          padding: 6px 16px;
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.7);
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .weather-toggle-btn.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .weather-toggle-btn:hover:not(.active) {
          color: white;
        }
        .snow-total-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: rgba(255,255,255,0.15);
          padding: 10px 16px;
          border-radius: 10px;
          margin-top: 12px;
        }
        .snow-total-icon {
          font-size: 1.25rem;
        }
        .snow-total-text {
          font-size: 1rem;
          font-weight: 600;
        }
        .hourly-forecast-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 12px;
        }
        .hourly-day-group {
          background: rgba(0,0,0,0.1);
          border-radius: 10px;
          padding: 12px;
        }
        .hourly-day-label {
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          opacity: 0.9;
          margin-bottom: 10px;
          padding-left: 4px;
        }
        .weather-forecast.hourly {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          justify-content: flex-start;
        }
        .weather-forecast.hourly .forecast-day {
          min-width: 48px;
          flex: 0 0 auto;
        }
        @media (max-width: 480px) {
          .weather-current {
            flex-direction: column;
            text-align: center;
          }
          .weather-temp-main {
            align-items: center;
          }
          .weather-details {
            align-items: center;
          }
        }
      `}</style>

      <PullToRefreshIndicator />

      {/* Main Content */}
      <div className="page-container">
        {/* Welcome */}
        <div className="page-header">
          <h1 className="page-title">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-muted">
            {user?.role === 'owner' && 'Owner'}
            {user?.role === 'admin' && 'Administrator'}
            {user?.role === 'crew_lead' && 'Crew Lead'}
            {user?.role === 'driver' && 'Driver'}
          </p>
        </div>

        {/* Trial Status Banner */}
        {isOnTrial() && (
          <div className={`trial-banner ${isTrialExpired() ? 'trial-expired' : ''}`}>
            <div className="trial-banner-content">
              <span className="trial-icon">{isTrialExpired() ? '⚡' : '🎉'}</span>
              <div className="trial-text">
                {isTrialExpired() ? (
                  <>
                    <strong>Trial Complete!</strong> You've used your {TRIAL_STORM_LIMIT} free service visit.
                  </>
                ) : (
                  <>
                    <strong>Free Trial</strong> - {getStormEventsUsed()}/{TRIAL_STORM_LIMIT} service visits used
                  </>
                )}
              </div>
              {isTrialExpired() ? (
                <Link to="/pricing" className="trial-upgrade-btn">Upgrade Now</Link>
              ) : (
                <span className="trial-remaining">{TRIAL_STORM_LIMIT - getStormEventsUsed()} remaining</span>
              )}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{loading ? '-' : stats.totalRoutes}</div>
            <div className="stat-label">Total Routes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: stats.activeRoutes > 0 ? 'var(--color-secondary)' : undefined }}>
              {loading ? '-' : stats.activeRoutes}
            </div>
            <div className="stat-label">Active Routes</div>
          </div>
          {!isDriver() && (
            <div className="stat-card">
              <div className="stat-value">{loading ? '-' : stats.totalCustomers}</div>
              <div className="stat-label">Customers</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-value">{loading ? '-' : stats.pendingStops}</div>
            <div className="stat-label">Total Stops</div>
          </div>
        </div>

        {/* Weather Widget */}
        <div className="weather-widget">
          {/* Weather location header */}
          {!weatherLoading && !weatherError && weather && (
            <div className="weather-header">
              <span className="weather-header-location">
                📍 {weatherLocationName || weather.current?.location || 'Weather'}
              </span>
              {weatherSettings?.weatherUseHome && weatherSettings?.weatherCity && (
                <button
                  className="weather-location-toggle"
                  onClick={usingCurrentLocation ? handleUseHomeLocation : handleUseCurrentLocation}
                  title={usingCurrentLocation ? 'Use home location' : 'Use current GPS location'}
                >
                  {usingCurrentLocation ? '🏠 Home' : '📍 Current'}
                </button>
              )}
            </div>
          )}
          {weatherLoading ? (
            <div className="weather-loading">
              <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
              <span>Loading weather...</span>
            </div>
          ) : weatherError ? (
            <div className="weather-error">
              <span className="weather-error-icon">☁️</span>
              <span>{weatherError}</span>
            </div>
          ) : weather ? (
            <>
              <div className="weather-current">
                <img
                  src={getWeatherIcon(weather.current.icon)}
                  alt={weather.current.condition}
                  className="weather-icon-large"
                />
                <div className="weather-temp-main">
                  <span className="weather-temp">{weather.current.temp}°F</span>
                  <span className="weather-condition">{weather.current.description}</span>
                  {weather.current.snow > 0 && (
                    <span className="weather-snow-current">❄️ {weather.current.snow}" snow</span>
                  )}
                </div>
                <div className="weather-details">
                  <span className="weather-detail">Feels like {weather.current.feelsLike}°F</span>
                  <span className="weather-detail">Wind: {weather.current.windSpeed} mph</span>
                </div>
              </div>

              {/* Daily/Hourly Toggle */}
              <div className="weather-toggle">
                <button
                  className={`weather-toggle-btn ${weatherView === 'daily' ? 'active' : ''}`}
                  onClick={() => setWeatherView('daily')}
                >
                  Daily
                </button>
                <button
                  className={`weather-toggle-btn ${weatherView === 'hourly' ? 'active' : ''}`}
                  onClick={() => setWeatherView('hourly')}
                >
                  Hourly
                </button>
              </div>

              {/* Daily Forecast */}
              {weatherView === 'daily' && weather.forecast && weather.forecast.length > 0 && (
                <>
                  <div className="weather-forecast">
                    {weather.forecast.slice(0, 7).map((day, index) => (
                      <div key={index} className="forecast-day">
                        <span className="forecast-label">{formatForecastDate(day.date)}</span>
                        <img
                          src={getWeatherIcon(day.icon)}
                          alt={day.condition}
                          className="forecast-icon"
                        />
                        <span className="forecast-temp">{day.temp}°</span>
                        {parseFloat(day.rain) > 0 && (
                          <span className="forecast-rain">🌧️ {day.rain}"</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Hourly Forecast - Grouped by Day */}
              {weatherView === 'hourly' && weather.hourly && weather.hourly.length > 0 && (
                <>
                  <div className="hourly-forecast-container">
                    {groupHourlyByDay(weather.hourly).map((dayGroup, dayIndex) => (
                      <div key={dayIndex} className="hourly-day-group">
                        <div className="hourly-day-label">{dayGroup.label}</div>
                        <div className="weather-forecast hourly">
                          {dayGroup.hours.map((hour, index) => (
                            <div key={index} className="forecast-day">
                              <span className="forecast-label">{formatHourlyTime(hour.time)}</span>
                              <img
                                src={getWeatherIcon(hour.icon)}
                                alt={hour.condition}
                                className="forecast-icon"
                              />
                              <span className="forecast-temp">{hour.temp}°</span>
                              {parseFloat(hour.snow) > 0 && (
                                <span className="forecast-snow">❄️ {hour.snow}"</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>

        {/* Quick Actions */}
        <h2 className="mt-lg mb-md">Quick Actions</h2>
        <div className="grid grid-2">
          <div className="mobile-card" onClick={() => navigate('/service-mode')} style={{ cursor: 'pointer' }}>
            <div className="mobile-card-header">
              <div>
                <h3 className="mobile-card-title" style={{ color: 'var(--color-secondary)' }}>Service Mode</h3>
                <p className="mobile-card-subtitle">Start servicing routes</p>
              </div>
            </div>
            <button className="btn btn-secondary btn-block">Start Service Mode</button>
          </div>

          <div className="mobile-card" onClick={() => navigate('/routes')} style={{ cursor: 'pointer' }}>
            <div className="mobile-card-header">
              <div>
                <h3 className="mobile-card-title">Routes</h3>
                <p className="mobile-card-subtitle">View and manage routes</p>
              </div>
            </div>
            <button className="btn btn-primary btn-block">View Routes</button>
          </div>

          {!isDriver() && (
            <div className="mobile-card" onClick={() => navigate('/customers')} style={{ cursor: 'pointer' }}>
              <div className="mobile-card-header">
                <div>
                  <h3 className="mobile-card-title">Customers</h3>
                  <p className="mobile-card-subtitle">Manage customer list</p>
                </div>
              </div>
              <button className="btn btn-primary btn-block">View Customers</button>
            </div>
          )}

          {isAdmin() && (
            <div className="mobile-card financial-summary-card">
              <div className="mobile-card-header financial-header">
                <div>
                  <h3 className="mobile-card-title">
                    <span className="financial-icon">💰</span> Financial Summary
                  </h3>
                </div>
                <select
                  className="financial-period-select"
                  value={financialPeriod}
                  onChange={handleFinancialPeriodChange}
                >
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_season">This Season</option>
                  <option value="ytd">Year to Date</option>
                  <option value="all_time">All Time</option>
                </select>
              </div>
              {financialSummaryLoading ? (
                <div className="financial-summary-loading">Loading...</div>
              ) : financialSummaryError ? (
                <div className="financial-summary-loading" style={{ color: '#dc2626' }}>{financialSummaryError}</div>
              ) : financialSummary ? (
                <div className="financial-summary-stats">
                  <div className="financial-section">
                    <div className="financial-section-label">Revenue</div>
                    <div className="financial-row">
                      <span className="financial-label">Paid:</span>
                      <span className="financial-value financial-paid">${formatCurrency(financialSummary.revenuePaid)}</span>
                    </div>
                    <div className="financial-row">
                      <span className="financial-label">Unpaid:</span>
                      <span className="financial-value financial-unpaid">${formatCurrency(financialSummary.revenueUnpaid)}</span>
                    </div>
                    <div className="financial-row financial-row-total">
                      <span className="financial-label">Total:</span>
                      <span className="financial-value">${formatCurrency(financialSummary.revenueTotal)}</span>
                    </div>
                  </div>
                  <div className="financial-row financial-row-expense">
                    <span className="financial-label">Expenses:</span>
                    <span className="financial-value financial-expense">-${formatCurrency(financialSummary.expensesTotal)}</span>
                  </div>
                  <div className="financial-row financial-row-profit">
                    <span className="financial-label">Net Profit:</span>
                    <span className={`financial-value ${financialSummary.netProfit >= 0 ? 'financial-profit-positive' : 'financial-profit-negative'}`}>
                      {financialSummary.netProfit >= 0 ? '' : '-'}${formatCurrency(Math.abs(financialSummary.netProfit))}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="financial-summary-loading">No data available</div>
              )}
              <Link to="/reports" className="btn btn-outline btn-block financial-link">
                View Details →
              </Link>
            </div>
          )}
        </div>

        {/* Trial Notice (if applicable) */}
        {user?.subscriptionTier === 'FREE_TRIAL' && user?.trialEndsAt && (
          <div className="alert alert-info mt-lg">
            <strong>Free Trial Active</strong> - Your trial ends on{' '}
            {new Date(user.trialEndsAt).toLocaleDateString()}.
            Upgrade to continue using {getAppName()}.
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
